import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { weekendApi } from "../../../../api/weekend";
import { WMCoreParts, WMSchedule, WMScheduleInput } from "../../../../types/scheduling/weekend";
import { updateWMSchedulesCache } from "../../../../query/meetings";
import { HGBugsnagNotify } from "../../../../helpers/bugsnag";

export interface UsePartAssignmentProps {
  part: WMCoreParts;
  schedule: WMSchedule;
  langGroupId: number;
  onRefresh?: () => void;
}

export function usePartAssignment({ part, schedule, langGroupId, onRefresh }: UsePartAssignmentProps) {
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const setScheduleMutation = useMutation({
    mutationFn: (wmsInput: WMScheduleInput) => weekendApi.setSchedule(wmsInput, langGroupId),
  });

  const autoFillSingleMutation = useMutation({
    mutationFn: (autoPart: { date: string; part: WMCoreParts }) =>
      weekendApi.autoSingleCRH(autoPart.date, autoPart.part, langGroupId),
  });

  // Create a schedule input object with the current values
  const createScheduleInput = (userId: number): WMScheduleInput => {
    const input: WMScheduleInput = {
      date: schedule.date,
      opening_song: schedule.opening_song?.number ?? null,
      wm_chairman: schedule.wm_chairman,
      temp_cong: schedule.temp_cong?.id ?? 0,
      speaker: schedule.speaker?.id ?? 0,
      speaker2: schedule.speaker2 ?? null,
      public_talk: schedule.public_talk?.number ?? 0,
      talk_mod: schedule.talk_mod ?? "",
      confirmed: schedule.confirmed,
      wt_conductor: schedule.wt_conductor ?? null,
      wm_reader: schedule.wm_reader,
      openprayer: schedule.openprayer ?? null,
      closeprayer: schedule.closeprayer ?? null,
      interpreter: schedule.interpreter,
      host: schedule.host,
    };

    // Set the part value based on the part type
    switch (part) {
      case WMCoreParts.Chairman:
        input.wm_chairman = userId;
        break;
      case WMCoreParts.WTConductor:
        input.wt_conductor = userId || null;
        break;
      case WMCoreParts.Reader:
        input.wm_reader = userId;
        break;
      case WMCoreParts.OpeningPrayer:
        input.openprayer = userId || null;
        break;
      case WMCoreParts.ClosingPrayer:
        input.closeprayer = userId || null;
        break;
      case WMCoreParts.Interpreter:
        input.interpreter = userId;
        break;
      case WMCoreParts.Host:
        input.host = userId;
        break;
    }

    return input;
  };

  // Assign a user to this part
  const assignPart = async (userId: number) => {
    setSaving(true);
    const input = createScheduleInput(userId);

    try {
      const resp = await setScheduleMutation.mutateAsync(input);
      updateWMSchedulesCache(queryClient, langGroupId, resp);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      console.error("wm crh: error setting assignment", err);
      HGBugsnagNotify("wmCRHSetAssignment", err, input);
    } finally {
      setSaving(false);
    }
  };

  // Auto-assign this part
  const autoAssignPart = async () => {
    setSaving(true);
    const autoSingle = { date: schedule.date, part };

    try {
      const resp = await autoFillSingleMutation.mutateAsync(autoSingle);
      updateWMSchedulesCache(queryClient, langGroupId, resp);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      console.error("wm crh: error auto-assigning single", err);
      HGBugsnagNotify("wmCRHAutoAssignSingle", err);
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    assignPart,
    autoAssignPart,
  };
}
